/**
 * `/payments/method` - PAYMENT DETAILS (design 25, T22): `getPayoutMethod`, `updatePayoutMethod`,
 * `listAgreements` and `acceptAgreements` for the payout documents, `listCountries`, and
 * `getPaymentsView` for the payout rule the intro states.
 *
 * Register R-005 (lens findings C1-01, U1-01, C4-01) made this screen: until it, nothing answered
 * here and a new author could never be paid (the run holds an author with no method as
 * `no_payout_method`). Design 25 made it the author's payment record.
 *
 * WHAT IT SHOWS (design 25 section 6; the owner's D1-D5, 2026-10-11). A header that says "Complete"
 * or "Not complete" and, while it is not, what is missing in words and the day it is needed by; then
 * three cards - who we pay (the full legal name and the country of residence, picked from the
 * served list and named in the cabinet's language), where we send the money (the PayPal email, IN FULL
 * here: D5 - the Payments card keeps the mask), and the confirmation (one checkbox per record,
 * never pre-ticked: "these details are true", ticked again on every save, and each payout document
 * that has a version in force, its page a new tab away). It opens as a summary with "Change"; an
 * empty record opens on the form. It is not a menu entry (D1): Payments' top card, the Author
 * profile's card and the Overview's row lead here.
 *
 * A SAVE (design 25, 3.2). The form is checked when the author saves, each message says what to do,
 * and focus moves to the first. Then `PUT /v1/payout-method` - a new version of the record - and,
 * for the payout documents ticked, ONE `acceptAgreements`. The record is read back after both,
 * because completeness counts the documents too.
 *
 * ★ THE ONE RE-AUTHENTICATION IN THE CABINET. A write whose token was signed in more than the
 * server's window ago is refused 403 `REAUTH_REQUIRED`. The answer is `./reauthenticate.ts`: the
 * session's own popup provider, a forced token refresh, and the write retried ONCE; an e-mail-link
 * session is signed out and sent to sign in again. The re-authentication is a BUTTON, not automatic,
 * because a browser opens a popup only inside a click. What was typed waits in component state
 * across the popup and nowhere else - never the URL, never browser storage - and is dropped once
 * saved or once the person has to leave the page. The acceptances wait for the retried write.
 *
 * NO CLOCK, NO FIGURE WRITTEN IN (./payment-details.ts): the intro's minimum and currency and the
 * needed-by line's "today" are the payments view's.
 */
import {
  Alert,
  Anchor,
  Button,
  Card,
  Checkbox,
  Group,
  Select,
  Stack,
  Text,
  TextInput,
  VisuallyHidden,
} from '@mantine/core';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import type { ReactNode, RefObject } from 'react';
import { useRouter } from '@tanstack/react-router';
import { hasErrorCode, useCabinetApi } from '../../features/cabinet/api';
import type { ErrorCode } from '../../features/cabinet/api';
import type {
  Agreement,
  AgreementAcceptRequest,
  CountryList,
  PaymentDetails,
  PaymentDetailsWrite,
} from '../../features/cabinet/api/cabinet-api';
import { ArrowLeft } from '@moonx/ui';
import {
  CabinetPageHeader,
  ErrorState,
  LoadingState,
  PartialFailureNote,
  SectionLabel,
} from '../../features/cabinet/components';
import { useAsync } from '../../features/cabinet/state';
import { formatInstant } from '../../features/cabinet/format';
import { authClient } from '../../features/auth/firebase/create-auth-client';
import { signInPathFor } from '../../features/auth/navigation';
import { useAuthSession } from '../../features/auth/session/AuthSessionProvider';
import { looksLikeEmailAddress } from '../../features/auth/session/magic-link-address';
import { InAppAnchor } from '../../features/shell/InAppAnchor';
import {
  FORM_ERRORS,
  acceptedLive,
  completenessWords,
  countryName,
  countryOptions,
  dayOfInstant,
  documentWords,
  isComplete,
  missingLine,
  payoutDocuments,
  payoutIntro,
  requiredFromLine,
} from './payment-details';
import type { FormError } from './payment-details';
import { reauthMethodFor, reauthenticateWithPopup } from './reauthenticate';
import type { ReauthMethod, ReauthOutcome } from './reauthenticate';
import { Trans, useTranslation } from '../../i18n';
import classes from './payments.module.css';

/** This screen's address. Payments, Author profile and the Overview link here. */
export const PAYOUT_METHOD_PATH = '/payments/method';

const PAYMENTS_PATH = '/payments';

/** Typed, so a code the contract does not have cannot be spelled here. */
const REAUTH_REQUIRED: ErrorCode = 'REAUTH_REQUIRED';

/** Longest legal name the server takes (design 25, 3.2). */
const LEGAL_NAME_MAX = 200;

/** One save: the record's new version, then the documents ticked with it. */
interface PendingSave {
  readonly write: PaymentDetailsWrite;
  readonly accept: AgreementAcceptRequest['accept'];
}

/** How the last attempt to confirm went, when it did not confirm. */
type ReauthNote = Exclude<ReauthOutcome['kind'], 'confirmed' | 'switched-account'>;

type Phase =
  | { readonly kind: 'editing' }
  | { readonly kind: 'saving' }
  /** Refused for an old sign-in. `note` says how the last attempt to confirm went, if it failed. */
  | { readonly kind: 'reauth'; readonly save: PendingSave; readonly note?: ReauthNote }
  | { readonly kind: 'reauthenticating'; readonly save: PendingSave }
  /** Saved. `agreementError` when the record was saved and the documents' acceptance was not. */
  | { readonly kind: 'saved'; readonly method: PaymentDetails; readonly agreementError?: unknown }
  | { readonly kind: 'failed'; readonly error: unknown; readonly stillRefused: boolean }
  | { readonly kind: 'switched-account' };

type PopupReauth = Extract<ReauthMethod, { kind: 'popup' }>;

/** The fields the check names, in the order the page shows them - focus goes to the first. */
type Field = 'legalName' | 'country' | 'email' | 'confirm';
const FIELD_ORDER: readonly Field[] = ['legalName', 'country', 'email', 'confirm'];

/** Each field's message, as its place in the catalogue (`FORM_ERRORS`); translated where shown. */
type FieldErrors = Partial<Record<Field, FormError>>;

interface Draft {
  readonly legalName: string;
  readonly country: string | null;
  readonly email: string;
  readonly confirmed: boolean;
}

function check(draft: Draft): FieldErrors {
  const errors: { -readonly [K in Field]?: FormError } = {};
  const legalName = draft.legalName.trim();
  if (legalName === '') {
    errors.legalName = FORM_ERRORS.legalName;
  } else if (legalName.length > LEGAL_NAME_MAX) {
    errors.legalName = FORM_ERRORS.legalNameTooLong;
  }
  if (draft.country === null || draft.country === '') {
    errors.country = FORM_ERRORS.country;
  }
  if (!looksLikeEmailAddress(draft.email)) {
    errors.email = FORM_ERRORS.email;
  }
  if (!draft.confirmed) {
    errors.confirm = FORM_ERRORS.confirm;
  }
  return errors;
}

export function PayoutMethodRoute() {
  const { t } = useTranslation('payments');
  const api = useCabinetApi();
  const router = useRouter();
  const { state, signOut } = useAuthSession();

  const readMethod = useCallback((signal: AbortSignal) => api.getPayoutMethod(signal), [api]);
  const readAgreements = useCallback((signal: AbortSignal) => api.listAgreements(signal), [api]);
  const readCountries = useCallback((signal: AbortSignal) => api.listCountries(signal), [api]);
  const readPayments = useCallback((signal: AbortSignal) => api.getPaymentsView(signal), [api]);
  const method = useAsync(readMethod, 'payments:payout-method');
  const agreements = useAsync(readAgreements, 'payments:agreements');
  const countries = useAsync(readCountries, 'payments:countries');
  // Only for the rule the intro states and "today"; a refusal (an account on hold) costs nothing.
  const payments = useAsync(readPayments, 'payments:view');
  const { refetch: refetchMethod } = method;
  const { refetch: refetchAgreements } = agreements;

  /** "Change" was pressed. An empty record, or one that did not load, is on the form anyway. */
  const [changing, setChanging] = useState(false);
  /** `null` until the person types, so each field shows what is on file. */
  const [legalNameDraft, setLegalNameDraft] = useState<string | null>(null);
  const [countryDraft, setCountryDraft] = useState<string | null>(null);
  const [emailDraft, setEmailDraft] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [ticked, setTicked] = useState<Readonly<Record<string, boolean>>>({});
  const [errors, setErrors] = useState<FieldErrors>({});
  const [phase, setPhase] = useState<Phase>({ kind: 'editing' });

  const legalNameRef = useRef<HTMLInputElement>(null);
  const countryRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const confirmRef = useRef<HTMLInputElement>(null);

  // Set in the effect BODY, not only cleared in its cleanup: under StrictMode the effect runs,
  // cleans up and runs again, and a flag that only its cleanup touched would stay false for good.
  const live = useRef(true);
  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
    };
  }, []);

  const onFile = method.data;
  const documents = payoutDocuments(agreements.data?.items);
  const draft: Draft = {
    legalName: legalNameDraft ?? onFile?.legalName ?? '',
    country: countryDraft ?? onFile?.recipientCountry ?? null,
    email: emailDraft ?? onFile?.paypal?.email ?? '',
    confirmed,
  };

  /** A field's message goes as soon as the person changes that field. */
  const clearError = (field: Field) => {
    setErrors((current) => {
      if (current[field] === undefined) {
        return current;
      }
      const next = { ...current };
      delete next[field];
      return next;
    });
  };

  /** Back to what is on file: what was typed leaves the page's memory. */
  const dropDraft = useCallback(() => {
    setLegalNameDraft(null);
    setCountryDraft(null);
    setEmailDraft(null);
    setConfirmed(false);
    setTicked({});
    setErrors({});
  }, []);

  const submit = useCallback(
    async (save: PendingSave, afterReauth: boolean): Promise<void> => {
      setPhase({ kind: 'saving' });
      let saved: PaymentDetails;
      try {
        saved = await api.updatePayoutMethod(save.write);
      } catch (error) {
        if (!live.current) {
          return;
        }
        if (hasErrorCode(error, REAUTH_REQUIRED) && !afterReauth) {
          setPhase({ kind: 'reauth', save });
          return;
        }
        // A second REAUTH_REQUIRED right after a fresh sign-in is shown, never answered with
        // another popup: one retry, as the contract says, and no loop.
        setPhase({ kind: 'failed', error, stillRefused: hasErrorCode(error, REAUTH_REQUIRED) });
        return;
      }
      let agreementError: unknown;
      if (save.accept.length > 0) {
        try {
          await api.acceptAgreements({ accept: save.accept });
        } catch (error) {
          agreementError = error;
        }
      }
      if (!live.current) {
        return;
      }
      // Written: what was typed has done its job and leaves the page's memory.
      dropDraft();
      setChanging(false);
      setPhase(
        agreementError === undefined
          ? { kind: 'saved', method: saved }
          : { kind: 'saved', method: saved, agreementError },
      );
      refetchMethod();
      refetchAgreements();
    },
    [api, dropDraft, refetchMethod, refetchAgreements],
  );

  const focus = (field: Field) => {
    const target = {
      legalName: legalNameRef,
      country: countryRef,
      email: emailRef,
      confirm: confirmRef,
    }[field];
    target.current?.focus();
  };

  const onSave = () => {
    const found = check(draft);
    setErrors(found);
    const first = FIELD_ORDER.find((field) => found[field] !== undefined);
    if (first !== undefined) {
      focus(first);
      return;
    }
    void submit(
      {
        write: {
          methodKind: 'paypal',
          recipientCountry: draft.country,
          legalName: draft.legalName.trim(),
          detailsTrue: true,
          paypal: { email: draft.email.trim() },
        },
        // Only a version in force can be accepted; one with none (`live` null) is never ticked.
        accept: documents.flatMap((document) =>
          document.live !== null &&
          !acceptedLive(document) &&
          ticked[document.documentCode] === true
            ? [{ documentCode: document.documentCode, versionLabel: document.live.versionLabel }]
            : [],
        ),
      },
      false,
    );
  };

  const confirmWithPopup = (save: PendingSave, reauth: PopupReauth) => {
    setPhase({ kind: 'reauthenticating', save });
    // No await before this call: the popup must open inside this click (./reauthenticate.ts).
    void reauthenticateWithPopup(authClient(), reauth.provider).then((outcome) => {
      if (!live.current) {
        return;
      }
      if (outcome.kind === 'confirmed') {
        void submit(save, true);
        return;
      }
      if (outcome.kind === 'switched-account') {
        dropDraft();
        setPhase({ kind: 'switched-account' });
        return;
      }
      setPhase({ kind: 'reauth', save, note: outcome.kind });
    });
  };

  /** Out of the page to sign in again. What was typed is dropped first, on purpose. */
  const signInAgain = () => {
    dropDraft();
    void signOut().then(() => {
      // A `replace`, as the gate's own move to the same address is: Back must not return to a
      // screen that would only send the person straight to sign in again.
      router.history.replace(signInPathFor(PAYOUT_METHOD_PATH));
    });
  };

  const busy = phase.kind === 'saving' || phase.kind === 'reauthenticating';
  const formOpen =
    changing ||
    phase.kind === 'reauth' ||
    phase.kind === 'reauthenticating' ||
    (onFile !== undefined && !onFile.isSet) ||
    (method.status === 'error' && onFile === undefined);
  const today = payments.data?.computedAt;
  const rule =
    payments.data === undefined
      ? undefined
      : {
          currency: payments.data.currency,
          thresholdMinor: payments.data.threshold.thresholdMinor,
        };

  return (
    <Stack gap="xl">
      <CabinetPageHeader
        title={t('method.title')}
        lede={payoutIntro(rule)}
        actions={onFile === undefined ? undefined : <CompletenessPill method={onFile} />}
      />
      <Group>
        <InAppAnchor href={PAYMENTS_PATH} size="sm">
          <Group gap={6} component="span" wrap="nowrap">
            <ArrowLeft aria-hidden="true" size={14} />
            {t('method.back')}
          </Group>
        </InAppAnchor>
      </Group>

      {method.status === 'loading' && onFile === undefined ? (
        <LoadingState label={t('method.reading')} />
      ) : method.status === 'error' && onFile === undefined ? (
        // Partial: the form below still works without it.
        <PartialFailureNote what={t('method.failed')} error={method.error} />
      ) : onFile === undefined ? null : (
        <WhatIsMissing method={onFile} today={today} />
      )}

      {onFile === undefined && method.status === 'loading' ? null : formOpen ? (
        <Stack gap="lg">
          <FormCards
            draft={draft}
            errors={errors}
            busy={busy}
            countries={countries.data}
            countriesFailed={countries.status === 'error' ? countries.error : undefined}
            refs={{ legalNameRef, countryRef, emailRef }}
            onLegalName={(value) => {
              setLegalNameDraft(value);
              clearError('legalName');
            }}
            onCountry={(value) => {
              setCountryDraft(value);
              clearError('country');
            }}
            onEmail={(value) => {
              setEmailDraft(value);
              clearError('email');
            }}
          />
          <ConfirmationCard
            confirmed={confirmed}
            error={errors.confirm}
            busy={busy}
            documents={documents}
            agreementsFailed={agreements.status === 'error' ? agreements.error : undefined}
            ticked={ticked}
            confirmRef={confirmRef}
            onConfirmed={(value) => {
              setConfirmed(value);
              clearError('confirm');
            }}
            onTicked={(code, value) => {
              setTicked((current) => ({ ...current, [code]: value }));
            }}
          />
          <Stack gap={6}>
            <Group>
              <Button onClick={onSave} loading={phase.kind === 'saving'} disabled={busy}>
                {t('method.save')}
              </Button>
              {onFile?.isSet === true ? (
                <Button
                  variant="default"
                  disabled={busy}
                  onClick={() => {
                    dropDraft();
                    setChanging(false);
                    setPhase({ kind: 'editing' });
                  }}
                >
                  {t('method.cancel')}
                </Button>
              ) : null}
            </Group>
            <Text size="xs" c="dimmed">
              {t('method.signInAgainLater')}
            </Text>
          </Stack>
        </Stack>
      ) : onFile === undefined ? null : (
        <Summary
          method={onFile}
          countries={countries.data}
          documents={documents}
          agreementsFailed={agreements.status === 'error' ? agreements.error : undefined}
          onChange={() => {
            dropDraft();
            setChanging(true);
            setPhase({ kind: 'editing' });
          }}
        />
      )}

      {phase.kind === 'reauth' || phase.kind === 'reauthenticating' ? (
        <ReauthPanel
          reauth={reauthMethodFor(state.identity)}
          busy={phase.kind === 'reauthenticating'}
          note={phase.kind === 'reauth' ? phase.note : undefined}
          onConfirm={(reauth) => {
            confirmWithPopup(phase.save, reauth);
          }}
          onSignInAgain={signInAgain}
        />
      ) : null}

      {phase.kind === 'saved' ? (
        <Stack gap="sm">
          <Alert variant="light" role="status" title={t('method.saved.title')}>
            <Text size="sm">
              <Trans
                t={t}
                i18nKey="method.saved.body"
                values={{
                  address:
                    phase.method.paypal?.email ??
                    phase.method.paypal?.maskedEmail ??
                    t('method.saved.addressYouGave'),
                }}
                components={{
                  strong: <strong />,
                  // Trans puts the sentence's own link words inside it. Not `link`: that is a void
                  // HTML element, and Trans would draw it empty with the words after it.
                  anchor: (
                    <InAppAnchor href={PAYMENTS_PATH} size="sm">
                      {null}
                    </InAppAnchor>
                  ),
                }}
              />
            </Text>
          </Alert>
          {phase.agreementError === undefined ? null : (
            <ErrorState
              title={t('method.saved.agreementNotRecorded')}
              error={phase.agreementError}
            />
          )}
        </Stack>
      ) : null}

      {phase.kind === 'failed' ? (
        phase.stillRefused ? (
          <Alert variant="light" role="alert" title={t('method.stillRefused.title')}>
            <Stack gap="xs" align="flex-start">
              <Text size="sm">{t('method.stillRefused.body')}</Text>
              <Button size="xs" variant="default" onClick={signInAgain}>
                {t('method.signOutAndIn')}
              </Button>
            </Stack>
          </Alert>
        ) : (
          <ErrorState title={t('method.notSaved')} error={phase.error} />
        )
      ) : null}

      {phase.kind === 'switched-account' ? (
        <Alert variant="light" role="alert" title={t('method.switchedAccount.title')}>
          <Text size="sm">{t('method.switchedAccount.body')}</Text>
        </Alert>
      ) : null}
    </Stack>
  );
}

/* ------------------------------------------------------------------------------ the parts */

/** "Complete" or "Not complete", the payout pills' shape: the words, then the tint. */
export function CompletenessPill({ method }: { readonly method: PaymentDetails }) {
  const complete = isComplete(method);
  return (
    <span
      className={`${classes.pill ?? ''} ${(complete ? classes.pillDone : classes.pillHeld) ?? ''}`}
    >
      <span className={classes.pillDot} aria-hidden="true" />
      {completenessWords(complete)}
    </span>
  );
}

/** While not complete: what is missing, by when, and - for an empty record - that nothing is lost. */
function WhatIsMissing({
  method,
  today,
}: {
  readonly method: PaymentDetails;
  readonly today: string | undefined;
}) {
  const { t } = useTranslation('payments');
  if (isComplete(method)) {
    return null;
  }
  const missing = missingLine(method.missing);
  const needed = requiredFromLine(method.requiredFrom, today);
  if (missing === undefined && needed === undefined && method.isSet) {
    return null;
  }
  return (
    <Alert variant="light" role="status" title={missing ?? t('method.notComplete')}>
      <Stack gap={4}>
        {needed === undefined ? null : <Text size="sm">{needed}</Text>}
        {method.isSet ? null : <Text size="sm">{t('method.untilYouAdd')}</Text>}
      </Stack>
    </Alert>
  );
}

/** One fact of the summary: its name and its value, or the quiet words when there is none. */
function Fact({ name, children }: { readonly name: string; readonly children: ReactNode }) {
  return (
    <div className={classes.fact}>
      <dt className={classes.factName}>{name}</dt>
      <dd className={classes.factValue}>{children}</dd>
    </div>
  );
}

function NotGiven() {
  const { t } = useTranslation('payments');
  return (
    <Text size="sm" c="dimmed" component="span">
      {t('method.notGiven')}
    </Text>
  );
}

/** A card named by its small-capitals heading, as the rest of the cabinet draws them. */
function NamedCard({ title, children }: { readonly title: string; readonly children: ReactNode }) {
  const id = useId();
  return (
    <Card padding="lg" component="section" aria-labelledby={id}>
      <SectionLabel id={id}>{title}</SectionLabel>
      {children}
    </Card>
  );
}

function Summary({
  method,
  countries,
  documents,
  agreementsFailed,
  onChange,
}: {
  readonly method: PaymentDetails;
  readonly countries: CountryList | undefined;
  readonly documents: readonly Agreement[];
  readonly agreementsFailed: unknown;
  readonly onChange: () => void;
}) {
  const { t } = useTranslation('payments');
  const country = method.recipientCountry;
  const served = countries?.items.find((item) => item.code === country)?.label;
  const email = method.paypal?.email ?? method.paypal?.maskedEmail;
  return (
    <Stack gap="lg">
      <NamedCard title={t('method.whoWePay')}>
        <dl className={classes.facts}>
          <Fact name={t('method.legalName.label')}>
            {method.legalName === undefined ||
            method.legalName === null ||
            method.legalName === '' ? (
              <NotGiven />
            ) : (
              method.legalName
            )}
          </Fact>
          <Fact name={t('method.country.label')}>
            {country === undefined || country === null ? (
              <NotGiven />
            ) : (
              countryName(country, served)
            )}
          </Fact>
        </dl>
      </NamedCard>
      <NamedCard title={t('method.whereWeSend')}>
        <dl className={classes.facts}>
          <Fact name={t('method.paypalEmail.label')}>
            {email === undefined || email === null ? <NotGiven /> : email}
          </Fact>
        </dl>
      </NamedCard>
      <NamedCard title={t('method.yourConfirmation')}>
        <Stack gap="sm">
          <Text size="sm">
            {method.detailsConfirmedAt === undefined || method.detailsConfirmedAt === null
              ? t('method.notConfirmed')
              : t('method.confirmedOn', { day: dayOfInstant(method.detailsConfirmedAt) })}
          </Text>
          {agreementsFailed === undefined ? null : (
            <PartialFailureNote what={t('method.agreementsFailed')} error={agreementsFailed} />
          )}
          {documents.length === 0 ? null : (
            <dl className={classes.facts}>
              {documents.map((document) => (
                <Fact key={document.documentCode} name={documentWords(document).name}>
                  <DocumentState document={document} />
                </Fact>
              ))}
            </dl>
          )}
        </Stack>
      </NamedCard>
      <Group gap="md">
        <Button variant="default" onClick={onChange}>
          {t('method.change')}
        </Button>
        {method.updatedAt === undefined || method.updatedAt === null ? null : (
          <Text size="xs" c="dimmed">
            {t('method.lastChanged', { when: formatInstant(method.updatedAt) })}
          </Text>
        )}
      </Group>
    </Stack>
  );
}

/** A payout document in the summary: accepted on which day, a newer version, or not yet - and its page. */
function DocumentState({ document }: { readonly document: Agreement }) {
  const { t } = useTranslation('payments');
  const accepted = document.accepted;
  return (
    <Group gap="sm" wrap="wrap">
      <span>
        {accepted === null
          ? t('method.document.notAccepted')
          : acceptedLive(document)
            ? t('method.document.acceptedOn', { day: dayOfInstant(accepted.acceptedAt) })
            : t('method.document.newVersion')}
      </span>
      <DocumentLink document={document} />
    </Group>
  );
}

/**
 * The document's own page on moonx.app, in a new tab - a different site from the cabinet. None for a
 * document with no version in force (`live` null): there is nothing in force to read.
 */
function DocumentLink({ document }: { readonly document: Agreement }) {
  const { t } = useTranslation('payments');
  if (document.live === null) {
    return null;
  }
  return (
    <Anchor href={document.live.contentUrl} target="_blank" rel="noopener noreferrer" size="sm">
      <Trans
        t={t}
        i18nKey="method.document.link"
        values={{ read: documentWords(document).read }}
        components={{ hidden: <VisuallyHidden /> }}
      />
    </Anchor>
  );
}

/** The form's first two cards: who we pay, and where the money goes. */
function FormCards({
  draft,
  errors,
  busy,
  countries,
  countriesFailed,
  refs,
  onLegalName,
  onCountry,
  onEmail,
}: {
  readonly draft: Draft;
  readonly errors: FieldErrors;
  readonly busy: boolean;
  readonly countries: CountryList | undefined;
  readonly countriesFailed: unknown;
  readonly refs: {
    readonly legalNameRef: RefObject<HTMLInputElement | null>;
    readonly countryRef: RefObject<HTMLInputElement | null>;
    readonly emailRef: RefObject<HTMLInputElement | null>;
  };
  readonly onLegalName: (value: string) => void;
  readonly onCountry: (value: string | null) => void;
  readonly onEmail: (value: string) => void;
}) {
  const { t } = useTranslation('payments');
  return (
    <>
      <NamedCard title={t('method.whoWePay')}>
        <Stack gap="md">
          <TextInput
            ref={refs.legalNameRef}
            label={t('method.legalName.label')}
            description={t('method.legalName.description')}
            autoComplete="name"
            maxLength={LEGAL_NAME_MAX}
            value={draft.legalName}
            onChange={(event) => {
              onLegalName(event.currentTarget.value);
            }}
            error={errors.legalName === undefined ? undefined : t(errors.legalName)}
            disabled={busy}
            required
          />
          <Select
            ref={refs.countryRef}
            label={t('method.country.label')}
            description={t('method.country.description')}
            searchable
            allowDeselect={false}
            nothingFoundMessage={t('method.country.nothingFound')}
            data={countryOptions(countries, draft.country)}
            value={draft.country}
            onChange={onCountry}
            error={errors.country === undefined ? undefined : t(errors.country)}
            disabled={busy}
            required
          />
          {countriesFailed === undefined ? null : (
            <PartialFailureNote what={t('method.country.listFailed')} error={countriesFailed} />
          )}
        </Stack>
      </NamedCard>
      <NamedCard title={t('method.whereWeSend')}>
        <TextInput
          ref={refs.emailRef}
          type="email"
          label={t('method.paypalEmail.label')}
          description={t('method.paypalEmail.description')}
          autoComplete="email"
          value={draft.email}
          onChange={(event) => {
            onEmail(event.currentTarget.value);
          }}
          error={errors.email === undefined ? undefined : t(errors.email)}
          disabled={busy}
          required
        />
      </NamedCard>
    </>
  );
}

/**
 * "Your confirmation": ONE CHECKBOX PER RECORD, NEVER PRE-TICKED (design 25 section 6). "These
 * details are true" is a statement saved with this version (P13), so it is ticked on every save;
 * each payout document is accepted once per version, so one whose version in force is accepted
 * says so instead of asking again - as does one with no version in force, accepted before (no link).
 */
function ConfirmationCard({
  confirmed,
  error,
  busy,
  documents,
  agreementsFailed,
  ticked,
  confirmRef,
  onConfirmed,
  onTicked,
}: {
  readonly confirmed: boolean;
  readonly error: FormError | undefined;
  readonly busy: boolean;
  readonly documents: readonly Agreement[];
  readonly agreementsFailed: unknown;
  readonly ticked: Readonly<Record<string, boolean>>;
  readonly confirmRef: RefObject<HTMLInputElement | null>;
  readonly onConfirmed: (value: boolean) => void;
  readonly onTicked: (code: string, value: boolean) => void;
}) {
  const { t } = useTranslation('payments');
  const id = useId();
  return (
    <Card padding="lg" component="section" aria-labelledby={id}>
      <SectionLabel id={id}>{t('method.yourConfirmation')}</SectionLabel>
      <fieldset className={classes.fieldset} aria-labelledby={id} disabled={busy}>
        <Stack gap="md">
          <Checkbox
            ref={confirmRef}
            label={t('method.detailsTrue')}
            checked={confirmed}
            onChange={(event) => {
              onConfirmed(event.currentTarget.checked);
            }}
            error={error === undefined ? undefined : t(error)}
          />
          {documents.map((document) => {
            const words = documentWords(document);
            return acceptedLive(document) && document.accepted !== null ? (
              <Stack key={document.documentCode} gap={2}>
                <Text size="sm">{words.agree}</Text>
                <Group gap="sm" wrap="wrap">
                  <Text size="xs" c="dimmed">
                    {t('method.document.acceptedOn', {
                      day: dayOfInstant(document.accepted.acceptedAt),
                    })}
                  </Text>
                  <DocumentLink document={document} />
                </Group>
              </Stack>
            ) : (
              <Checkbox
                key={document.documentCode}
                label={words.agree}
                description={<DocumentLink document={document} />}
                checked={ticked[document.documentCode] === true}
                onChange={(event) => {
                  onTicked(document.documentCode, event.currentTarget.checked);
                }}
              />
            );
          })}
          {agreementsFailed === undefined ? null : (
            <PartialFailureNote what={t('method.agreementsFailed')} error={agreementsFailed} />
          )}
        </Stack>
      </fieldset>
    </Card>
  );
}

function ReauthPanel({
  reauth,
  busy,
  note,
  onConfirm,
  onSignInAgain,
}: {
  readonly reauth: ReauthMethod;
  readonly busy: boolean;
  readonly note: ReauthNote | undefined;
  readonly onConfirm: (reauth: PopupReauth) => void;
  readonly onSignInAgain: () => void;
}) {
  const { t } = useTranslation('payments');
  return (
    <Alert variant="light" role="alert" title={t('method.reauth.title')}>
      <Stack gap="sm" align="flex-start">
        {reauth.kind === 'popup' ? (
          <>
            <Text size="sm">{t('method.reauth.popup')}</Text>
            <Button
              size="sm"
              loading={busy}
              onClick={() => {
                onConfirm(reauth);
              }}
            >
              {t('method.reauth.confirmWith', { provider: reauth.label })}
            </Button>
          </>
        ) : (
          <>
            <Text size="sm">
              {reauth.kind === 'email-link'
                ? t('method.reauth.emailLink')
                : t('method.reauth.signInAgain')}
            </Text>
            <Button size="sm" variant="default" onClick={onSignInAgain}>
              {t('method.signOutAndIn')}
            </Button>
          </>
        )}
        {note === undefined ? null : (
          <Text size="sm" c="dimmed">
            {t(`method.reauth.notes.${note}`)}
          </Text>
        )}
      </Stack>
    </Alert>
  );
}
